// File: frontend/src/lib/firebase.ts
// Updated to fix service worker registration path

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// VAPID key for web push
export const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging
let messaging: any = null;

// Check if messaging is supported (only in secure contexts)
const initializeMessaging = async () => {
  try {
    const supported = await isSupported();
    if (supported && 'serviceWorker' in navigator) {
      // Register service worker first, then initialize messaging
      await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/app/'
      });
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      messaging = getMessaging(app);
      console.log('Firebase messaging initialized successfully');
    }
  } catch (error) {
    console.error('Error initializing Firebase messaging:', error);
  }
};

// Initialize messaging
initializeMessaging();

/**
 * Request notification permission and get FCM token
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // Check if messaging is available
    if (!messaging) {
      console.warn('Firebase messaging not initialized yet, retrying...');
      await initializeMessaging();
      
      if (!messaging) {
        console.error('Firebase messaging failed to initialize');
        return null;
      }
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted.');
      
      try {
        // Get registration token with the registered service worker
        const registration = await navigator.serviceWorker.getRegistration('/app/');
        
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration
        });
        
        if (token) {
          console.log('✅ FCM Registration token:', token);
          return token;
        } else {
          console.log('No registration token available.');
          return null;
        }
      } catch (tokenError) {
        console.error('Error getting token:', tokenError);
        
        // Fallback: try without explicit service worker registration
        try {
          const fallbackToken = await getToken(messaging, {
            vapidKey: VAPID_KEY,
          });
          
          if (fallbackToken) {
            console.log('✅ FCM Registration token (fallback):', fallbackToken);
            return fallbackToken;
          }
        } catch (fallbackError) {
          console.error('Fallback token generation failed:', fallbackError);
        }
        
        return null;
      }
    } else {
      console.log('Unable to get permission to notify.');
      return null;
    }
  } catch (error) {
    console.error('An error occurred while retrieving token:', error);
    return null;
  }
};

/**
 * Handle foreground messages
 */
export const onMessageListener = () => {
  return new Promise((resolve) => {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      return;
    }

    onMessage(messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Show manual notification for foreground messages
      if (Notification.permission === 'granted') {
        new Notification(
          payload.notification?.title || 'New Notification',
          {
            body: payload.notification?.body || '',
            icon: payload.notification?.icon || '/app/pwa-192x192.png',
            tag: payload.data?.booking_id || 'notification',
            data: payload.data
          }
        );
      }
      
      resolve(payload);
    });
  });
};

/**
 * Get current FCM token
 */
export const getCurrentToken = async (): Promise<string | null> => {
  try {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      await initializeMessaging();
      
      if (!messaging) {
        return null;
      }
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
    });
    
    return token || null;
  } catch (error) {
    console.error('Error getting current token:', error);
    return null;
  }
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = (): boolean => {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
};

/**
 * Get notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'denied';
};

export { messaging };
export default app;